"use client"

import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../../Auth/context/AuthContext" // Real AuthContext
import { Plus, Trash2, Edit, Save, X, Loader2 } from "lucide-react"
import axios from 'axios'
import type { Note } from "../types"
//import { mockNotes } from "../mockData" // Removed mock notes

interface ContactNotesProps {
  contactId: string
}

export default function ContactNotes({ contactId }: ContactNotesProps) {
  const { auth } = useContext(AuthContext)
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState("")
  const [editingNote, setEditingNote] = useState<{ id: string; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  // Fetch notes from API
  useEffect(() => {
    const fetchNotes = async () => {
      if (!auth?.token) return;
      setLoading(true);
      try {
        const resp = await axios.get(
          `/api/myteconstruction/contacts/${contactId}/notes`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setNotes(resp.data.data);
      } catch (err) {
        console.error("Error fetching notes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [contactId, auth?.token]);

  const handleAddNote = () => {
    if (!newNote.trim() || saving) return

    setSaving(true)

    // Call API to add note
    const add = async () => {
      try {
        const resp = await axios.post(
          `/api/myteconstruction/contacts/${contactId}/notes`,
          { text: newNote },
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setNotes(prev => [resp.data.data, ...prev]);
        setNewNote("");
      } catch (err) {
        console.error("Error adding note:", err);
      } finally {
        setSaving(false);
      }
    };
    add();
  }

  const handleUpdateNote = () => {
    if (!editingNote || !editingNote.text.trim() || saving) return

    setSaving(true)

    // Call API to update note
    const update = async () => {
      try {
        const resp = await axios.put(
          `/api/myteconstruction/notes/${editingNote.id}`,
          { text: editingNote.text },
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setNotes(prev => prev.map(note => note._id === editingNote.id ? resp.data.data : note));
        setEditingNote(null);
      } catch (err) {
        console.error("Error updating note:", err);
      } finally {
        setSaving(false);
      }
    };
    update();
  }

  const handleDeleteNote = (noteId: string) => {
    if (!window.confirm("Are you sure you want to delete this note?") || saving) return

    setSaving(true)

    // Call API to delete note
    const remove = async () => {
      try {
        await axios.delete(
          `/api/myteconstruction/notes/${noteId}`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setNotes(prev => prev.filter(note => note._id !== noteId));
      } catch (err) {
        console.error("Error deleting note:", err);
      } finally {
        setSaving(false);
      }
    };
    remove();
  }

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-300 mb-2">Notes</h3>

      {/* Add Note Form */}
      <div className="bg-gray-900 rounded-md p-3 mb-4">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          className="w-full bg-gray-800 text-white rounded-md p-2 border border-gray-700 mb-2 min-h-[80px]"
          disabled={saving}
        />
        <div className="flex justify-end">
          <button
            onClick={handleAddNote}
            disabled={!newNote.trim() || saving}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" />
                <span>Add Note</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notes List */}
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : notes.length > 0 ? (
        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
          {notes.map((note) => (
            <div key={note._id} className="bg-gray-900 rounded-md p-3 border border-gray-800">
              {editingNote && editingNote.id === note._id ? (
                <div>
                  <textarea
                    value={editingNote.text}
                    onChange={(e) => setEditingNote({ ...editingNote, text: e.target.value })}
                    className="w-full bg-gray-800 text-white rounded-md p-2 border border-gray-700 mb-2 min-h-[80px]"
                    disabled={saving}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingNote(null)}
                      className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-xs flex items-center gap-1"
                      disabled={saving}
                    >
                      <X className="h-3 w-3" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleUpdateNote}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-xs flex items-center gap-1 disabled:opacity-50"
                      disabled={!editingNote.text.trim() || saving}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-3 w-3" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start">
                    <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => setEditingNote({ id: note._id, text: note.text })}
                        className="p-1 hover:bg-gray-700 rounded-md transition-colors"
                        title="Edit note"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note._id)}
                        className="p-1 hover:bg-gray-700 rounded-md transition-colors"
                        title="Delete note"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 flex justify-between">
                    <span>
                      {new Date(note.createdAt).toLocaleDateString()} at{" "}
                      {new Date(note.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {note.updatedAt !== note.createdAt && <span>(edited)</span>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No notes yet. Add one to keep track of important information.</p>
      )}
    </div>
  )
}
